import { Flag } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../base/alert";
import { cn } from "../../lib/utils";

/** Warning code emitted by the backend for each active flagged timeline entry (OTO-156). */
export const DEVICE_FLAGGED_WARNING_CODE = "device_flagged";

export interface DeviceWarningLike {
  code: string;
  severity: string;
  message: string;
}

export interface DeviceFlagBannerProps {
  /** The device's `warnings` array, straight from the device response. */
  warnings?: DeviceWarningLike[] | null;
  className?: string;
}

/** Pull the flag warnings out of a device's warning list. */
export function getFlagWarnings(warnings?: DeviceWarningLike[] | null): DeviceWarningLike[] {
  return (warnings ?? []).filter((w) => w.code === DEVICE_FLAGGED_WARNING_CODE);
}

/**
 * The backend prefixes each flag warning with "Flagged: " so it reads correctly in the
 * generic device-warnings list, where it sits alongside unrelated codes. A surface that
 * already says "flagged" in its own chrome should strip it rather than repeat it.
 *
 * Single source of truth for that convention — every flag-specific surface calls this.
 */
export function stripFlagPrefix(message: string): string {
  return message.replace(/^Flagged:\s*/, "");
}

/**
 * Flagged-device banner (OTO-156).
 *
 * An operator flags a timeline entry; every device lookup then has to lead with that
 * warning. This sits at the very top of the device detail screen, above the health banner.
 * Renders nothing when the device carries no flags — like {@link DeviceHealthBanner}, an
 * un-flagged device shouldn't reserve space for a warning that isn't there.
 */
export function DeviceFlagBanner({ warnings, className }: DeviceFlagBannerProps) {
  const flags = getFlagWarnings(warnings);
  if (flags.length === 0) return null;

  return (
    <Alert variant="alert" className={cn(className)} role="alert">
      <Flag className="h-4 w-4" />
      <AlertTitle className="font-display">
        {flags.length === 1 ? "This device is flagged" : `This device has ${flags.length} flags`}
      </AlertTitle>
      <AlertDescription>
        {flags.length === 1 ? (
          <span>{stripFlagPrefix(flags[0].message)}</span>
        ) : (
          <ul className="list-disc space-y-0.5 pl-4">
            {flags.map((flag, index) => (
              <li key={`${flag.code}-${index}`}>{stripFlagPrefix(flag.message)}</li>
            ))}
          </ul>
        )}
      </AlertDescription>
    </Alert>
  );
}
